import { CircleNotch } from "@phosphor-icons/react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { getApiErrorMessage, useCreateHousehold } from "@/lib/api";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateHouseholdDialog({ open, onOpenChange }: Props) {
  const createMutation = useCreateHousehold();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [address, setAddress] = useState("");
  const [error, setError] = useState("");

  const resetForm = () => {
    setName("");
    setContactEmail("");
    setAddress("");
    setError("");
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const data = await createMutation.mutateAsync({
        name,
        contact_email: contactEmail,
        address: address || null,
      });
      resetForm();
      onOpenChange(false);
      if (data) {
        void navigate(`/admin/households/${data.id}`);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to create household."));
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New household</DialogTitle>
          <DialogDescription>Create a pre-provisioned household.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="householdName" className="text-foreground text-sm font-medium">
              Name
            </label>
            <Input
              id="householdName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="householdEmail" className="text-foreground text-sm font-medium">
              Contact email
            </label>
            <Input
              id="householdEmail"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="householdAddress" className="text-foreground text-sm font-medium">
              Address <span className="text-muted">(optional)</span>
            </label>
            <Input
              id="householdAddress"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
          {error && (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <CircleNotch className="h-4 w-4 animate-spin" weight="light" />
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
